import { useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { insideSingleLevelSameOriginIframe } from ':fat/helpers/iframe';
import { SecurityFrame } from ':fat/messaging/entities/securityFrame';
import { initializeMessageBus } from ':fat/messaging/MessageBus';
import { AppRoutes } from ':fat/routing/routes';

import { SignerContext } from './SecurityFrameSignerContext';

// SecurityFrameSidecar is always rendered inside the security frame, no matter
// what route it's on. It doesn't have any display, but is implemented as a
// a component so it can navigate and control things withing the React rendering
// context. It is responsible for two things:
//
//   - Ensuring it is actually running inside an iframe with proper nesting on
//     the correct origin, and redirecting the parent if that's not the case
//   - Receiving and handling messages from the main page
//
export function SecurityFrameSidecar() {
  useEnsureInsideSecurityFrame();

  const { signer } = useContext(SignerContext);
  const navigate = useNavigate();

  const handlerContext: SecurityFrame.HandlerContext = useMemo(
    () => ({
      navigate,
      signer,
    }),
    [navigate, signer],
  );

  useEffect(() => {
    initializeMessageBus('securityFrame', handlerContext);

    window.DEVTOOL_STATE = {};
    if (process.env.RELEASE_STAGE === 'development') {
      window.DEVTOOL_STATE.testnet = true;
    }

    return () => MessageBus.stopListening();
    // We don't want to re-initialize when the context values change, so
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    MessageBus.setHandlerContext('securityFrame', handlerContext);
  }, [handlerContext]);

  return null;
}

function useEnsureInsideSecurityFrame() {
  useEffect(() => {
    if (!insideSingleLevelSameOriginIframe()) {
      // eslint-disable-next-line no-console
      console.error('Security frame loaded improperly. Redirecting to start page.');
      document.location.href = window.origin + AppRoutes.SECURITY_FRAME_LOADER;
    }
  }, []);
}
